import React, { useState, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import PropdocsModal from '../modal/PropdocsModal';
import { useNotifications } from '../../notifications';
import { LINK_AMENDMENT_TO_BASE, GET_BASE_DOCUMENTS_IN_DEAL } from '../../api/mutations';
import { getDocumentById, getDocumentsByDeal, GET_EXTRACTED_DATA_BY_TEAM } from '../../api/queries_v2';
import styles from './LinkAmendmentModal.module.scss';

interface BaseDocument {
  id: string;
  title: string;
}

interface LinkAmendmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  amendmentId: string;
  amendmentTitle: string;
  dealId: string;
  documentId?: string;
  teamId?: string;
  documentType?: string;
  onSuccess?: () => void;
}

export const LinkAmendmentModal: React.FC<LinkAmendmentModalProps> = ({
  isOpen,
  onClose,
  amendmentId,
  amendmentTitle,
  dealId,
  documentId,
  teamId,
  documentType,
  onSuccess,
}) => {
  const [selectedBaseDocId, setSelectedBaseDocId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // Query to fetch base documents in the deal
  const [fetchBaseDocuments, { data: baseDocsData, loading: baseDocsLoading }] = useLazyQuery(
    GET_BASE_DOCUMENTS_IN_DEAL,
    {
      fetchPolicy: 'network-only',
    }
  );

  // Mutation to link amendment
  const [linkAmendment] = useMutation(LINK_AMENDMENT_TO_BASE, {
    refetchQueries: [
      // Refetch document queries if documentId provided
      ...(documentId ? [{
        query: getDocumentById,
        variables: { documentId },
      }] : []),
      // Refetch deal documents
      {
        query: getDocumentsByDeal,
        variables: { dealId },
      },
      // Refetch DataPage query if teamId and documentType are provided
      ...(teamId && documentType ? [{
        query: GET_EXTRACTED_DATA_BY_TEAM,
        variables: { teamId, documentType },
      }] : []),
    ],
    onCompleted: async () => {
      notifySuccess({ message: 'Amendment successfully linked to base document' });
      setIsSubmitting(false);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to link amendment' });
      setIsSubmitting(false);
    },
  });

  // Fetch base documents when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBaseDocuments({ variables: { dealId } });
      setSelectedBaseDocId(''); // Reset selection
    }
  }, [isOpen, fetchBaseDocuments]);

  const handleSubmit = async () => {
    if (!selectedBaseDocId) {
      notifyError({ message: { defaultMessage: 'Please select a base document' } });
      return;
    }

    setIsSubmitting(true);
    await linkAmendment({
      variables: {
        amendmentId,
        baseDocumentId: selectedBaseDocId,
      },
    });
  };

  const baseDocuments: BaseDocument[] = baseDocsData?.baseDocumentsInDeal || [];

  return (
    <PropdocsModal
      show={isOpen}
      setShow={onClose}
      title={<span>Link Amendment to Base Document</span>}
      size="md"
    >
      <>
        <div className={styles.modalContent}>
          <div className={styles.infoSection}>
            <p className={styles.amendmentName}>
              <strong>Amendment:</strong> {amendmentTitle}
            </p>
            <p className={styles.helpText}>
              Select the base document that this amendment modifies:
            </p>
          </div>

          {baseDocsLoading ? (
            <div className={styles.loadingState}>Loading base documents...</div>
          ) : baseDocuments.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No base documents found in this deal.</p>
              <p className={styles.emptyHint}>
                Base documents must have extracted data and be classified as "base" type.
              </p>
            </div>
          ) : (
            <div className={styles.selectContainer}>
              <label htmlFor="baseDocSelect" className={styles.selectLabel}>
                Base Document
              </label>
              <select
                id="baseDocSelect"
                className={styles.selectDropdown}
                value={selectedBaseDocId}
                onChange={(e) => setSelectedBaseDocId(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">-- Select a base document --</option>
                {baseDocuments.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.buttonGroup}>
            <button
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </button>
            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedBaseDocId || baseDocuments.length === 0}
              type="button"
            >
              {isSubmitting ? 'Linking...' : 'Link Amendment'}
            </button>
          </div>
        </div>
      </>
    </PropdocsModal>
  );
};
